import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, AlertTriangle, Bus, Download, Search, RefreshCw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_COLORS = {
  am_missing: "bg-red-100 text-red-800 border-red-300",
  pm_missing: "bg-red-100 text-red-800 border-red-300",
  am_bus_no_number: "bg-amber-100 text-amber-800 border-amber-300",
  pm_bus_no_number: "bg-amber-100 text-amber-800 border-amber-300",
  pm_aftercare_with_bus: "bg-orange-100 text-orange-800 border-orange-300",
  am_morningcare_with_bus: "bg-orange-100 text-orange-800 border-orange-300",
  am_bus_with_nonbus_method: "bg-rose-100 text-rose-800 border-rose-300",
  pm_bus_with_nonbus_method: "bg-rose-100 text-rose-800 border-rose-300",
};

const ISSUE_SHORT = {
  am_missing: "AM missing",
  pm_missing: "PM missing",
  am_bus_no_number: "AM Bus · no #",
  pm_bus_no_number: "PM Bus · no #",
  pm_aftercare_with_bus: "After Care + Bus #",
  am_morningcare_with_bus: "Morning Care + Bus #",
  am_bus_with_nonbus_method: "AM Bus # / non-bus method",
  pm_bus_with_nonbus_method: "PM Bus # / non-bus method",
};

export default function TransportAuditView({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/transport-audit`);
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/transport-audit/export`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transport-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* swallow */ }
  };

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (activeFilter && !r.issues.includes(activeFilter)) return false;
      if (!q) return true;
      const hay = `${r.first_name} ${r.last_name} ${r.preferred_name} ${r.group_code} ${r.group_name}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data, search, activeFilter]);

  return (
    <div className="space-y-4" data-testid="transport-audit-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="transport-audit-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="transport-audit-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!data || data.total_with_issues === 0} data-testid="transport-audit-download-csv">
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-2xl border-2 border-rose-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Bus className="w-5 h-5 text-rose-700" />
          <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider">Transport Audit · Bus Sheet vs Camper Profile</p>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Camper transport mismatches</h2>
        <p className="text-sm text-gray-600 mt-1">
          Detects internal contradictions in each camper's AM/PM transport data so admins can fix entries
          at the source (Google Bus Sheet → CampMinder).
        </p>
        {data && (
          <div className="flex flex-wrap gap-2 mt-4 text-sm">
            <span className="inline-flex items-center gap-1.5 bg-white/70 px-3 py-1 rounded-full text-gray-700">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> {data.total_audited} campers audited
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white/70 px-3 py-1 rounded-full text-gray-700">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> {data.total_with_issues} with issues
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500" data-testid="transport-audit-loading">Loading audit…</div>
      ) : !data ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
          Failed to load audit. Try refreshing.
        </div>
      ) : data.total_with_issues === 0 ? (
        <div className="text-center py-16 bg-emerald-50 rounded-2xl border-2 border-emerald-200" data-testid="transport-audit-clean">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
          <p className="text-lg font-semibold text-emerald-900">All transport data is clean.</p>
          <p className="text-sm text-emerald-700 mt-1">No mismatches detected across {data.total_audited} campers.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="transport-audit-issue-tiles">
            <button
              onClick={() => setActiveFilter("")}
              className={`text-left rounded-xl border-2 p-3 transition-colors ${activeFilter === "" ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white hover:border-indigo-300"}`}
              data-testid="transport-audit-filter-all"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">All issues</div>
              <div className="text-2xl font-bold text-gray-900">{data.total_with_issues}</div>
            </button>
            {data.issue_counts.map((c) => (
              <button
                key={c.code}
                onClick={() => setActiveFilter(c.code === activeFilter ? "" : c.code)}
                className={`text-left rounded-xl border-2 p-3 transition-colors ${activeFilter === c.code ? "border-rose-500 bg-rose-50" : "border-gray-200 bg-white hover:border-rose-300"}`}
                data-testid={`transport-audit-filter-${c.code}`}
              >
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{c.label}</div>
                <div className="text-2xl font-bold text-gray-900">{c.count}</div>
              </button>
            ))}
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search by name or group…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="transport-audit-search"
            />
          </div>

          <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="transport-audit-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <th className="px-4 py-3">Group</th>
                    <th className="px-4 py-3">Camper</th>
                    <th className="px-4 py-3">AM Method</th>
                    <th className="px-4 py-3">AM Bus</th>
                    <th className="px-4 py-3">PM Method</th>
                    <th className="px-4 py-3">PM Bus</th>
                    <th className="px-4 py-3">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-gray-500">No matches.</td>
                    </tr>
                  ) : filteredRows.map((r) => (
                    <tr key={r.camper_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`transport-audit-row-${r.camper_id}`}>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-mono">
                          {r.group_code || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {r.first_name} {r.last_name}
                        {r.group_name && <div className="text-xs text-gray-500 font-normal">{r.group_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.am_method || <span className="text-gray-400 italic">empty</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{r.am_bus || <span className="text-gray-400 italic">empty</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{r.pm_method || <span className="text-gray-400 italic">empty</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{r.pm_bus || <span className="text-gray-400 italic">empty</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.issues.map((code) => (
                            <Badge key={code} variant="outline" className={`text-[11px] ${ISSUE_COLORS[code] || ""}`}>
                              {ISSUE_SHORT[code] || code}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
              Showing {filteredRows.length} of {data.total_with_issues} campers with issues.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
